import { IObservableArray, makeAutoObservable, observable, runInAction } from 'mobx';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';

type Notification = {
    title?: string;
    message: SnackbarMessage;
    options?: OptionsObject;
};
type AddNotification = {
    (message: SnackbarMessage, title?: string, options?: OptionsObject): void;
    (message: SnackbarMessage, options?: OptionsObject): void;
    (message: SnackbarMessage, title?: string | OptionsObject, options?: OptionsObject): void;
};

export default class NotificationStore {
    readonly notifications: IObservableArray<Notification> = observable.array<Notification>([]);
    displayedNotifications: SnackbarKey[] = [];

    constructor(initialData?: Partial<NotificationStore>) {
        makeAutoObservable(this);
        Object.assign(this, initialData);
    }

    addNotification: AddNotification = (message: SnackbarMessage, title?: string | OptionsObject, options?: OptionsObject) => {
        const settings = typeof title === 'object' ? title : options;
        const hasSpecifiedKey = settings && (settings.key || settings.key === 0);
        const key = hasSpecifiedKey ? settings.key : new Date().getTime() + Math.random();
        const notification = {
            title: typeof title === 'string' ? title : undefined,
            message,
            options: {
                key,
                ...settings,
            },
        };
        runInAction(() => this.notifications.push(notification));
    };

    removeNotification = (notification: Notification): boolean => runInAction(() => this.notifications.remove(notification));

    error: AddNotification = (message: SnackbarMessage, title?: string | OptionsObject, options?: OptionsObject) => {
        this.addNotification(message, title, { variant: 'error', ...options });
    };

    warning: AddNotification = (message: SnackbarMessage, title?: string | OptionsObject, options?: OptionsObject) => {
        this.addNotification(message, title, { variant: 'warning', ...options });
    };

    info: AddNotification = (message: SnackbarMessage, title?: string | OptionsObject, options?: OptionsObject) => {
        this.addNotification(message, title, { variant: 'info', ...options });
    };

    success: AddNotification = (message: SnackbarMessage, title?: string | OptionsObject, options?: OptionsObject) => {
        this.addNotification(message, title, { variant: 'success', ...options });
    };

    hasDisplayed = (notification: Notification): boolean =>
        Boolean(notification?.options?.key && this.displayedNotifications.includes(notification.options.key));
}
